import React from "react";
import {
    buildSpaceCacheKeys,
    SpaceView,
    deleteItemInResponse,
    replaceItemInResponse,
    getDriveCacheKey,
    isStarredView,
    buildCollectionCacheKeys,
} from "./utils";
import {
    useQuery,
    useMutation,
    queryCache,
    QueryConfig,
    useInfiniteQuery,
    InfiniteQueryConfig,
    MutateConfig,
} from "react-query";
import { spaceItem } from "../services/space-item";
import {
    IListItemsRequest,
    IItemsPermissionsRequest,
    IMoveItemRequest,
    IAddItemRequest,
    IRenameItemRequest,
    IRestoreItemRequest,
    IDeleteItemRequest,
    IUpdateItemPermissionRequest,
    IToggleStarredItemRequest,
} from "../types/space-item";
import { MAX_LINEAGE_NODE, SHARED_WITH_ME_ROOT_ITEM_ID } from "../utils/constants";
import { IAllocationDimension } from "helpers/allocation-dimension";
import { ICollectionBrandProperties } from "helpers/collection-objects";

export const useInfiniteFetchItems = (
    merchantId: string,
    itemId: string,
    params?: IListItemsRequest,
    options?: Partial<InfiniteQueryConfig<nuorder.folder.v1.ListItemsResponse, string>>,
    isFolderSelection?: boolean
) => {
    const additionaKeys = {
        params,
        ...(isFolderSelection ? { isFolderSelection } : {}),
    };
    return useInfiniteQuery<nuorder.folder.v1.ListItemsResponse, string>(
        buildSpaceCacheKeys(merchantId, itemId, undefined, additionaKeys),
        (_pageKey, _filterKey, page_token: string) => {
            return spaceItem.getItems(merchantId, itemId, {
                ...(params || {}),
                ...(page_token ? { page_token: page_token } : {}),
            });
        },
        {
            ...options,
            getFetchMore: (lastResponse: nuorder.folder.v1.ListItemsResponse) => {
                return lastResponse.items?.length && lastResponse.next_page_token ? lastResponse.next_page_token : null;
            },
            cacheTime: 0,
        }
    );
};

export const useFetchItem = (
    merchantId: string,
    itemId: string,
    isExternalCall?: boolean,
    options?: QueryConfig<nuorder.folder.v1.Item>
) => {
    return useQuery<nuorder.folder.v1.Item>(
        buildSpaceCacheKeys(merchantId, itemId, undefined, { isParentSpace: true, isExternalCall }),
        () => spaceItem.getItem(merchantId, itemId),
        options
    );
};

export const useFetchCollectionItem = (
    params: { collectionId: string; merchantId: string; isCopying: boolean },
    options: QueryConfig<IAllocationDimension[]>
) => {
    return useQuery<IAllocationDimension[]>(
        buildCollectionCacheKeys(params),
        () => spaceItem.fetchCollectionItem(params.collectionId, params.merchantId),
        options
    );
};

export const useFetchCollectionBrandItem = (
    collectionId: string,
    merchantId: string,
    options: QueryConfig<ICollectionBrandProperties[]>
) => {
    return useQuery<ICollectionBrandProperties[]>(
        buildCollectionCacheKeys({ collectionId, merchantId }),
        () => spaceItem.fetchCollectionBrandItem(collectionId, merchantId),
        options
    );
};

const collectLineage = async (
    merchantId: string,
    itemId: string,
    rootItemId: string,
    maxLineage: number,
    lineage: Partial<nuorder.folder.v1.Item>[]
): Promise<Partial<nuorder.folder.v1.Item>[]> => {
    const item: Partial<nuorder.folder.v1.Item> = await spaceItem.getItem(merchantId, itemId).catch((err) => {
        if (!rootItemId && (err as any).code === 403) {
            return null; // Shared with me root
        }
        throw err;
    });

    // This is a shared item from merchant drive when you are also and admin
    if (item && (item.name === "__root__" || item.parent === "0")) {
        return lineage;
    }

    // Omit root
    if (item && item.id !== rootItemId) {
        lineage.unshift(item);
    }

    if (lineage.length < maxLineage) {
        if (item?.parent && item.parent !== rootItemId && item.parent !== "0") {
            return await collectLineage(merchantId, item.parent, rootItemId, maxLineage, lineage);
        }
    } else {
        if (item && item.parent !== rootItemId) {
            lineage.unshift({
                id: MAX_LINEAGE_NODE,
                name: MAX_LINEAGE_NODE,
            });
        }
    }

    return lineage;
};

// the lineage does not include root drives
export const useFetchLineageItem = (
    merchantId: string,
    itemId: string,
    rootItemId: string,
    maxLineage: number,
    isExternalCall?: boolean,
    options?: QueryConfig<Partial<nuorder.folder.v1.Item>[]>
) => {
    return useQuery<Partial<nuorder.folder.v1.Item>[]>(
        buildSpaceCacheKeys(merchantId, itemId, undefined, { isLineage: true, isExternalCall }),
        () => collectLineage(merchantId, itemId, rootItemId, maxLineage, []),
        options
    );
};

export const useFetchItemPermissions = (
    merchantId: string,
    itemId: string,
    options?: QueryConfig<nuorder.folder.v1.Permission[]>
) => {
    return useQuery<nuorder.folder.v1.Permission[]>(
        buildSpaceCacheKeys(merchantId, itemId, undefined, { isParentSpace: true, isPermissions: true }),
        () =>
            spaceItem.getItemPermissions(merchantId, itemId).then((responseJson) => {
                return responseJson.permissions;
            }),
        options
    );
};

export const useInfiniteFetchItemPermissions = (
    merchantId: string,
    itemId: string,
    params?: IItemsPermissionsRequest,
    options?: Partial<InfiniteQueryConfig<nuorder.folder.v1.ListPermissionsResponse, string>>
) => {
    return useInfiniteQuery<nuorder.folder.v1.ListPermissionsResponse, string>(
        buildSpaceCacheKeys(merchantId, itemId, undefined, { isParentSpace: true, isPermissions: true }),
        (_pageKey, _filterKey, page_token: string) => {
            return spaceItem.getItemPermissions(merchantId, itemId, {
                ...params,
                ...(page_token ? { page_token: page_token } : {}),
            });
        },
        {
            ...options,
            getFetchMore: (lastResponse: nuorder.folder.v1.ListPermissionsResponse) => {
                return lastResponse.permissions?.length && lastResponse.next_page_token?.length
                    ? lastResponse.next_page_token
                    : null;
            },
        }
    );
};

export const useDeleteItem = (mutationObject?: MutateConfig<nuorder.folder.v1.Item, Error>) => {
    return useMutation<nuorder.folder.v1.Item, Error, IDeleteItemRequest>(spaceItem.deleteItem, {
        onSuccess: (data: nuorder.folder.v1.Item, variables: IDeleteItemRequest) => {
            mutationObject?.onSuccess?.(data, variables);
        },
    });
};

export const useAddItem = (mutationObject?: MutateConfig<nuorder.folder.v1.Item, Error>) => {
    return useMutation<nuorder.folder.v1.Item, Error, IAddItemRequest>(spaceItem.addItem, {
        onSuccess: (data: nuorder.folder.v1.Item, variables: IAddItemRequest) => {
            mutationObject?.onSuccess?.(data, variables);
        },
    });
};

export const useRenameItem = (mutationObject?: MutateConfig<nuorder.folder.v1.Item, Error>) => {
    return useMutation<nuorder.folder.v1.Item, Error, IRenameItemRequest>(spaceItem.renameItem, {
        onSuccess: (data: nuorder.folder.v1.Item, variables: IRenameItemRequest) => {
            mutationObject?.onSuccess?.(data, variables);
        },
        onError: (error: Error, variables: IRenameItemRequest, snap) => {
            mutationObject?.onError?.(error, variables, snap);
        },
    });
};

export const useToggleStarredItem = (
    merchantId: string,
    itemId: string,
    memberId: string,
    view: SpaceView,
    rootItemId: string,
    params?: IListItemsRequest,
    mutationObject?: MutateConfig<nuorder.folder.v1.Item, Error>
) => {
    return useMutation<nuorder.folder.v1.Item, Error, IToggleStarredItemRequest>(spaceItem.toggleStarredItem, {
        ...mutationObject,
        onSuccess: (data: nuorder.folder.v1.Item, variables: IToggleStarredItemRequest) => {
            if (isStarredView(view) && !variables.addToStarred) {
                const cacheKey = getDriveCacheKey(view, merchantId, itemId, rootItemId, memberId, params);
                queryCache.invalidateQueries(cacheKey);
            }

            mutationObject?.onSuccess?.(data, variables);
        },
        onError: (error: Error, variables: IToggleStarredItemRequest, snap) => {
            mutationObject?.onError?.(error, variables, snap);
        },
    });
};

export const useMoveItem = (
    merchantId: string,
    memberId: string,
    view: SpaceView,
    rootItemId: string,
    params?: IListItemsRequest,
    onSuccess?: (itemName: string, parentName: string) => void
) => {
    return useMutation<nuorder.folder.v1.Item, Error, IMoveItemRequest>(spaceItem.moveItem, {
        onSuccess: (data: nuorder.folder.v1.Item, { parentName, itemParentId }: IMoveItemRequest) => {
            const cacheKey = getDriveCacheKey(view, merchantId, itemParentId, rootItemId, memberId, params);
            // disabling cache key to fetch all items again because the cache key switch between folders and  others
            queryCache.invalidateQueries(cacheKey);
            onSuccess && onSuccess(data.name, parentName);
        },
    });
};

export const useRestoreItem = (
    itemId: string,
    view: SpaceView,
    rootItemId: string,
    params?: IListItemsRequest,
    mutationObject?: MutateConfig<nuorder.folder.v1.Item, Error>
) => {
    return useMutation<nuorder.folder.v1.Item, Error, IRestoreItemRequest>(spaceItem.restoreItem, {
        ...mutationObject,
        onSuccess: (data: nuorder.folder.v1.Item, { merchantId, item }: IRestoreItemRequest) => {
            const cacheKey = getDriveCacheKey(view, merchantId, itemId, rootItemId, undefined, params);
            queryCache.setQueryData(cacheKey, (old: nuorder.folder.v1.ListItemsResponse[] = []) => {
                if (old?.length) {
                    return deleteItemInResponse(
                        old,
                        "items",
                        (items: nuorder.folder.v1.Item[], id: string) => items.findIndex((it) => it.id === id),
                        item.id,
                        (newItems: nuorder.folder.v1.Item[], index: number) => newItems.splice(index, 1)
                    );
                }

                return [];
            });
            // Data does not all the properties set, so for that reason use item from the input params
            mutationObject?.onSuccess?.(item, {});
        },
        onError: (error: Error, variables: IRestoreItemRequest, snap) => {
            mutationObject?.onError?.(error, variables, snap);
        },
    });
};

export const useCreateItemPermission = (merchantId: string, itemId: string) => {
    return useMutation(spaceItem.createItemPermission, {
        onSuccess: () => {
            queryCache.invalidateQueries(
                buildSpaceCacheKeys(merchantId, itemId, undefined, { isParentSpace: true, isPermissions: true })
            );
        },
    });
};

export const useUpdateItemPermission = (merchantId: string, itemId: string) => {
    return useMutation<nuorder.folder.v1.Item, Error, IUpdateItemPermissionRequest>(spaceItem.updateItemPermission, {
        onSuccess: (data: nuorder.folder.v1.Permission) => {
            queryCache.setQueryData(
                buildSpaceCacheKeys(merchantId, itemId, undefined, { isParentSpace: true, isPermissions: true }),
                (old: nuorder.folder.v1.ListPermissionsResponse[]) => {
                    return replaceItemInResponse(
                        old,
                        "permissions",
                        (items: nuorder.folder.v1.Permission[], id: string) =>
                            items.findIndex((it) => it.id === id && !it.inherited),
                        data.id,
                        (
                            newItems: nuorder.folder.v1.Permission[],
                            index: number,
                            dataToAdd: nuorder.folder.v1.Permission
                        ) => newItems.splice(index, 1, dataToAdd),
                        data
                    );
                }
            );
        },
    });
};

export const useDeleteItemPermission = (
    merchantId: string,
    itemId: string,
    permissionId: string,
    onSuccess?: () => void,
    onError?: () => void
) => {
    return useMutation(spaceItem.deleteItemPermission, {
        onSuccess: (_data, { permissionId: pId }) => {
            queryCache.setQueryData(
                buildSpaceCacheKeys(merchantId, itemId, undefined, { isParentSpace: true, isPermissions: true }),
                (old: nuorder.folder.v1.ListPermissionsResponse[]) => {
                    if (old?.length) {
                        return deleteItemInResponse(
                            old,
                            "permissions",
                            (items: nuorder.folder.v1.Permission[], id: string) =>
                                items.findIndex((it) => it.id === id && !it.inherited),
                            pId,
                            (newItems: nuorder.folder.v1.Permission[], index: number) => newItems.splice(index, 1)
                        );
                    }
                    return [];
                }
            );
            onSuccess && onSuccess();
        },
        onError: (_err, _variables, _onMutateValue) => {
            onError && onError();
        },
    });
};

export const useInfiniteUserSharedItems = (
    merchantId: string,
    memberId: string,
    params?: IListItemsRequest,
    options?: Partial<InfiniteQueryConfig<nuorder.folder.v1.ListItemsResponse, string>>,
    isFolderSelection?: boolean
) => {
    const additionaKeys = {
        params,
        ...(isFolderSelection ? { isFolderSelection } : {}),
    };
    return useInfiniteQuery<nuorder.folder.v1.ListItemsResponse, string>(
        buildSpaceCacheKeys(merchantId, undefined, memberId, additionaKeys),
        (_pageKey, _filterKey, page_token: string) => {
            return spaceItem.getUserSharedItems(merchantId, memberId, {
                ...(params || {}),
                ...(page_token ? { page_token: page_token } : {}),
            });
        },
        {
            ...options,
            getFetchMore: (lastResponse: nuorder.folder.v1.ListItemsResponse) => {
                return lastResponse.items?.length && lastResponse.next_page_token ? lastResponse.next_page_token : null;
            },
            cacheTime: 0,
        }
    );
};

export const useInfiniteFetchStarredItems = (
    merchantId: string,
    memberId: string,
    params?: IListItemsRequest,
    options?: Partial<InfiniteQueryConfig<nuorder.folder.v1.ListItemsResponse, string>>,
    isFolderSelection?: boolean
) => {
    const additionaKeys = {
        params,
        starredItems: true,
        ...(isFolderSelection ? { isFolderSelection } : {}),
    };
    return useInfiniteQuery<nuorder.folder.v1.ListItemsResponse, string>(
        buildSpaceCacheKeys(merchantId, undefined, memberId, additionaKeys),
        (_pageKey, _filterKey, page_token: string) => {
            return spaceItem.getStarredItems(merchantId, {
                ...(params || {}),
                ...(page_token ? { page_token: page_token } : {}),
            });
        },
        {
            ...options,
            getFetchMore: (lastResponse: nuorder.folder.v1.ListItemsResponse) => {
                return lastResponse.items?.length && lastResponse.next_page_token ? lastResponse.next_page_token : null;
            },
            cacheTime: 0,
        }
    );
};

export const useSpaceViewFetcher = (spaceView: SpaceView, rootItemId: string, isFolderSelection?: boolean) => {
    return React.useCallback(
        (
            merchantId: string,
            itemId?: string,
            memberId?: string,
            params?: IListItemsRequest,
            options?: Partial<InfiniteQueryConfig<nuorder.folder.v1.ListItemsResponse, string>>
        ) => {
            if (rootItemId && rootItemId !== itemId && itemId !== SHARED_WITH_ME_ROOT_ITEM_ID) {
                // eslint-disable-next-line  react-hooks/rules-of-hooks
                return useInfiniteFetchItems(merchantId, itemId, params, options, isFolderSelection);
            }

            switch (spaceView) {
                case SpaceView.SharedWithMe:
                    // eslint-disable-next-line react-hooks/rules-of-hooks
                    return useInfiniteUserSharedItems(merchantId, memberId, params, options, isFolderSelection);
                case SpaceView.Starred:
                    // eslint-disable-next-line react-hooks/rules-of-hooks
                    return useInfiniteFetchStarredItems(merchantId, null, params, options, isFolderSelection);
                case SpaceView.MerchantDrive:
                default:
                    // eslint-disable-next-line react-hooks/rules-of-hooks
                    return useInfiniteFetchItems(merchantId, itemId, params, options, isFolderSelection);
            }
        },
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [spaceView, rootItemId]
    );
};
